import { mapFromEntities } from 'mappings/activityMapping';
import { mapFromGroupMember } from 'mappings/groupMapping';
import { mapFromLogin } from 'mappings/loginMapping';
import { mapFromPartners, mapFromUsers, mapToUser } from 'mappings/userMapping';
import { UserSystemRoleDTO } from 'models/dto/auth/userRoleDTO';
import EntityDTO from 'models/dto/entityDTO';
import FeedbackDTO from 'models/dto/feedbackDTO';
import { GroupMembersDTO } from 'models/dto/groupMembersDTO';
import IdListDTO from 'models/dto/IdListDTO';
import { LogDTO } from 'models/dto/logDTO';
import LoginDTO from 'models/dto/loginDTO';
import PartnerDTO from 'models/dto/partnerDTO';
import UserDTO from 'models/dto/userDTO';
import Entity from 'models/entity';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import { GroupMember } from 'models/groupMembers';
import Login from 'models/login';
import Partner from 'models/partner';
import Tenant from 'models/tenant';
import User from 'models/user';
import AppError from 'utils/appError';
import { toApiDateTimeRequired } from 'utils/datetime';
import http from './httpService';

export async function apiSendFeedback(subject: string, msg: string, accessToken: string): Promise<void> {
  try {
    const feedback = new FeedbackDTO();
    feedback.message = msg;
    feedback.subject = subject;

    await http.post<FeedbackDTO>('/users/feedback', feedback, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiLoginUser(accessToken: string): Promise<Login | undefined> {
  try {
    const ar = await http.get<LoginDTO>('/users/login', http.getRequestConfig(accessToken));

    return mapFromLogin(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetUsers(
  accessToken: string,
  includeAppType: boolean,
  cache: GlobalDataCache,
): Promise<User[]> {
  try {
    const ar = await http.get<UserDTO[]>(`/users?includeAppType=${includeAppType}`, http.getRequestConfig(accessToken));

    return mapFromUsers(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetUsersForCache(accessToken: string, cache: GlobalDataCache): Promise<User[]> {
  try {
    const ar = await http.get<UserDTO[]>('/users/cache', http.getRequestConfig(accessToken));

    return mapFromUsers(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetPartners(accessToken: string): Promise<Partner[]> {
  try {
    const ar = await http.get<PartnerDTO[]>('/users/partners', http.getRequestConfig(accessToken));

    return mapFromPartners(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveUsers(selectedUsers: string[], accessToken: string): Promise<void> {
  try {
    const userListDTO: IdListDTO = new IdListDTO(selectedUsers);
    await http.post<IdListDTO>('/users/delete', userListDTO, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiSetLicense(
  selectedUsers: string[],
  enableLicense: boolean,
  accessToken: string,
): Promise<void> {
  try {
    const userListDTO: IdListDTO = new IdListDTO(selectedUsers);
    await http.post<IdListDTO>(
      `/users/license/?enable=${enableLicense}`,
      userListDTO,
      http.getRequestConfig(accessToken),
    );
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddUser(user: User, accessToken: string): Promise<void> {
  try {
    const userDTO = mapToUser(user);
    await http.post<UserDTO>('/users', userDTO, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiReplaceUser(oldUser: User, newUser: User, accessToken: string): Promise<void> {
  try {
    await http.post<UserDTO>(`/users/${oldUser.id}/replace/${newUser.id}`, null, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateUser(user: User, accessToken: string): Promise<void> {
  try {
    const userDTO = mapToUser(user);
    await http.put<UserDTO>(`/users`, userDTO, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetGroupMembership(
  accessToken: string,
  includeMembers: boolean = true,
): Promise<GroupMember[]> {
  try {
    const res = await http.get<GroupMembersDTO[]>(
      `/users/groups?includeMembers=${includeMembers}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromGroupMember(res.data ?? []);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetUserEntityRelationInfo(userId: string, accessToken: string): Promise<Entity[]> {
  try {
    const ar = await http.get<EntityDTO[]>(`/users/${userId}/entities`, http.getRequestConfig(accessToken));

    return mapFromEntities(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddProcessLog(logDto: LogDTO, accessToken: string): Promise<void> {
  try {
    await http.post(`/users/processlog`, logDto, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetOrgUnitUsers(
  orgUnit: Tenant,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<User[]> {
  try {
    const ar = await http.get<UserDTO[]>(`/users/orgunits/${orgUnit.id}`, http.getRequestConfig(accessToken));

    return mapFromUsers(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiReplaceAdmin(newAdminUserId: string, adminRoleId: number, accessToken: string): Promise<void> {
  try {
    const newAdminRoleDTO: UserSystemRoleDTO = {
      userId: newAdminUserId,
      roleId: adminRoleId,
    };

    await http.post(`/users/roles/replaceadmin`, newAdminRoleDTO, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetUserLogins(
  tenantId: string | undefined,
  refDate: Date | undefined,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<User[]> {
  try {
    let url = `/users/tenant/logins`;
    if (tenantId) {
      url += `?tenantId=${tenantId}`;
    }
    if (refDate) {
      url += `?refDate=${toApiDateTimeRequired(refDate)}`;
    }
    const ar = await http.get<UserDTO[]>(url, http.getRequestConfig(accessToken));

    return mapFromUsers(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiProcessEventSubscriptionsForTenant(
  tenantId: string,
  userIds: string[],
  accessToken: string,
): Promise<void> {
  try {
    const idList = new IdListDTO(userIds);

    await http.post(
      `/users/tenant/subscriptions/events/process?tenantId=${tenantId}`,
      idList,
      http.getRequestConfig(accessToken),
    );
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
